import type { SpotDetail } from "@divespots/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { literalCommas } from "./search.ts";
import { course, declinationAt } from "./bearing.ts";
import type { Course, LatLon } from "./bearing.ts";

/** An underwater object a course can start or end at (own objects, and in 3D the neighbours' too). */
export interface CourseObject {
  id: string;
  name: string;
  type: SpotDetail["objects"][number]["type"];
  location: LatLon;
  /** Resolved depth in metres; null when unknown. */
  depthM: number | null;
  /** The depth comes from the lakebed model, not a measurement ("≈"). */
  approx?: boolean | undefined;
  /** Set for objects of another spot. */
  spotName?: string | undefined;
}

const PARAM = "kurs";
const ID = /^[\w-]{1,64}$/;

/** `?kurs=<A>` (choosing the destination) or `?kurs=<A>,<B>` (the course from A to B). */
export function parseKurs(raw: string | null): { startId: string | null; endId: string | null } {
  const [a, b] = (raw ?? "").split(",");
  const startId = a && ID.test(a) ? a : null;
  const endId = startId && b && ID.test(b) ? b : null;
  return { startId, endId };
}

export const formatKurs = (a: string, b?: string | null): string => (b ? `${a},${b}` : a);

/** The course pair lives in the URL, so a course can be shared; every other parameter (`at`) is kept. */
export function useCourseSelection() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const { startId, endId } = useMemo(() => parseKurs(params.get(PARAM)), [params]);
  const write = useCallback(
    (value: string | null) => {
      // keep every other parameter (`at`)
      const next = new URLSearchParams(search);
      if (value) next.set(PARAM, value);
      else next.delete(PARAM);
      const qs = literalCommas(next);
      // the history state (the walk's trail) stays with the entry
      void navigate({ search: qs ? `?${qs}` : "" }, { replace: true, state });
    },
    [navigate, search, state],
  );
  return {
    startId,
    endId,
    /** Start a course at A: destination still to be chosen. */
    setStart: useCallback((id: string) => write(formatKurs(id)), [write]),
    setEnd: useCallback(
      (id: string) => {
        if (startId) write(formatKurs(startId, id));
      },
      [write, startId],
    ),
    swap: useCallback(() => {
      if (startId && endId) write(formatKurs(endId, startId));
    }, [write, startId, endId]),
    clear: useCallback(() => write(null), [write]),
  };
}

/** Declination of the start point (WMM2025, today), cached: it barely changes within 0.1 degree or a month. */
export function useDeclination(at: LatLon | null) {
  const month = new Date().toISOString().slice(0, 7);
  return useQuery({
    queryKey: ["declination", at ? at.lat.toFixed(1) : null, at ? at.lon.toFixed(1) : null, month],
    queryFn: () => declinationAt(at as LatLon),
    enabled: at !== null,
    staleTime: Infinity,
    retry: false,
  });
}

/** The finished course from A to B, or null while the declination is still loading (or a depth/pair is missing). */
export function useCourseResult(a: CourseObject | null, b: CourseObject | null): Course | null {
  const decl = useDeclination(a && b ? a.location : null);
  return useMemo(() => {
    if (!a || !b || decl.data === undefined) return null;
    return course(
      { location: a.location, depthM: a.depthM },
      { location: b.location, depthM: b.depthM },
      decl.data,
    );
  }, [a, b, decl.data]);
}
